import { Icon, Image, Tooltip, Typography } from '@smartgift/frontend-toolkit';
import { AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { configs } from '@hero/configs';
import { useRecognitionCreateModal } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useSidebar } from '@hero/store/hooks';

import SidebarAccount from './SidebarAccount';
import SidebarHelp from './SidebarHelp';
import { SidebarItems } from './SidebarItems';
import * as S from './style';

export function Sidebar({ hasAppNavigationHeader = true }) {
  const { t } = useTranslation();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  const theme = useTheme();

  const handleLogoClick = () => {
    if (window.PROVIDER_URL) window?.location?.assign(window.PROVIDER_URL);
    else navigate('/');
  };

  const navigationApps = t('navigationApps', { returnObjects: true });
  const hasNavigationHeader =
    hasAppNavigationHeader && Array.isArray(navigationApps) && navigationApps?.length;

  const { showRecognitionCreateModal } = useRecognitionCreateModal();

  function handleSidebarPlus() {
    if (configs?.general?.simplifyFlow) {
      navigate(ROUTE_PATHS.SEND_GIFT_OCCASIONS);
    } else {
      showRecognitionCreateModal();
    }
  }

  return (
    <AnimatePresence>
      <S.Sidebar isSidebarOpen={isSidebarOpen} hasNavigationHeader={hasNavigationHeader}>
        <S.SidebarHeader>
          <Link to={ROUTE_PATHS?.HOME}>
            <S.SidebarHeaderLogo>
              <Image
                src={theme.images.logoSmall}
                alt="Logo small"
                height={32}
                width={32}
                onClick={handleLogoClick}
                style={{ minWidth: 32 }}
              />
              {isSidebarOpen && (
                <S.SidebarHeaderLogoText>
                  <Image src={theme.images.logoText} alt="Logo text" width={110} height="32px" />
                </S.SidebarHeaderLogoText>
              )}
            </S.SidebarHeaderLogo>
          </Link>
          <S.SidebarHeaderButton onClick={toggleSidebar}>
            <Icon name={isSidebarOpen ? 'left-chevron' : 'right-chevron'} size="sm" />
          </S.SidebarHeaderButton>
        </S.SidebarHeader>

        <S.SidebarBody>
          <S.SidebarButton isSidebarOpen={isSidebarOpen} onClick={handleSidebarPlus}>
            <Tooltip
              size="sm"
              tooltipConfig={{
                placement: 'left',
                offset: [0, 12]
              }}
              content={t('createRecognition')}
            >
              <S.SidebarButtonIcon>
                <Icon name="add" />
              </S.SidebarButtonIcon>
            </Tooltip>

            {isSidebarOpen && (
              <Typography variant="body2Bold" color="inherit" lineClamp={1}>
                {t('sendOneTimeGifts')}
              </Typography>
            )}
          </S.SidebarButton>

          <SidebarItems />

          <S.SidebarBottom>
            {configs.teams.enabled && (
              <S.SidebarBottomItem>
                <S.SidebarBottomItemLogo>
                  <Image src="teamsShareIcon" alt="Microsoft temas logo" height="28px" />
                </S.SidebarBottomItemLogo>
                {isSidebarOpen && (
                  <S.SidebarBottomItemLabel>{t('microsoftTeams')}</S.SidebarBottomItemLabel>
                )}
              </S.SidebarBottomItem>
            )}
            <SidebarHelp isSidebarOpen={isSidebarOpen} />
            <SidebarAccount isOpen={isSidebarOpen} />
          </S.SidebarBottom>
        </S.SidebarBody>
      </S.Sidebar>
    </AnimatePresence>
  );
}

Sidebar.propTypes = {
  hasAppNavigationHeader: PropTypes.bool
};
